import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { Box, CircularProgress, Paper, Typography, Button } from '@mui/material'
import { isAccountManager, isSuperAdmin, isClient, isInfluencer } from '../../lib/roles'

/**
 * ProtectedRoute component ensures only authenticated users can access certain routes
 * Optionally restricts access based on user role
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string|null} props.allowedRole - Optional role restriction (admin, super_admin, client, influencer)
 */
export const ProtectedRoute = ({ children, allowedRole = null }) => {
  const { user, userProfile, loading, billingEntitlement } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!user || !userProfile) {
    return <Navigate to="/login" replace />
  }

  if (userProfile.onboarding_complete === false && userProfile.role !== 'influencer') {
    return <Navigate to="/complete-onboarding" replace />
  }

  const roleMatch = (requestedRole, profile) => {
    if (!requestedRole) return true
    if (requestedRole === 'admin') return isAccountManager(profile)
    if (requestedRole === 'super_admin') return isSuperAdmin(profile)
    if (requestedRole === 'client') return isClient(profile)
    if (requestedRole === 'influencer') return isInfluencer(profile)
    return profile?.role === requestedRole
  }

  if (allowedRole && !roleMatch(allowedRole, userProfile)) {
    const isSuper = isSuperAdmin(userProfile)
    const roleRoutes = {
      account_manager: '/admin',
      platform_admin: '/super-admin',
      super_admin: '/super-admin',
      client: '/client',
      influencer: '/influencer',
      admin: isSuper ? '/super-admin' : '/admin',
    }
    return <Navigate to={roleRoutes[userProfile.role] || '/login'} replace />
  }

  const paywallOn = billingEntitlement?.paywall_enabled === true
  const entitled = billingEntitlement?.entitled !== false
  const isPaymentsPath = location.pathname.startsWith('/admin/payments')

  if (paywallOn && !entitled && !isSuperAdmin(userProfile)) {
    if (isAccountManager(userProfile)) {
      if (!isPaymentsPath) {
        return <Navigate to="/admin/payments" replace />
      }
    } else {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh" p={3}>
          <Paper variant="outlined" sx={{ p: 4, maxWidth: 480 }}>
            <Typography variant="h6" gutterBottom>
              Workspace subscription required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This workspace does not have an active Social Box subscription. Ask your agency admin to renew or contact support.
            </Typography>
            <Button href="/login" variant="outlined">
              Back to login
            </Button>
          </Paper>
        </Box>
      )
    }
  }

  return children
}
